import dayjs from "dayjs";
import React from "react";
import ReactApexChart from "react-apexcharts";
import { Button, CheckTreePicker, FormGroup, Notification, } from "rsuite";
import axios from "../../../../utilities/axios";
import { EmployeeErrorColumnChart } from "../../Types/EmployeeErrorColumnChartType";
import { YearNode } from "../../Types/YearNodeType";
import styles from "./styles/EmployeeErrorPerMonthChart.module.scss";
import { CheckPickerSelectFilter } from "../../../../components/formfields/CheckPickerSelectFilter";
import { MethodsUtils } from "../../../../core/utils/MethodsUtils";
import handleGenerateHarmoniousColors from "../../utils/handleGenerateRandomColor";


interface EmployeeErrorPerMonthChartState {
    errors: { label: string; value: number, count: number }[];
    data: { count: number, value: number }[][];
    errorPerMonth: EmployeeErrorColumnChart;
    selectedEmployees: string[];
    selectedErrors: string[];
    selectedYear: string[];
    loadingButton: boolean;
    selectFullData: YearNode[];
}

interface EmployeeProps {
    employee: { value: number, label: String }[]
    errors: { value: number, label: String; hasValue: boolean; }[]
}

class EmployeeErrorPerMonthChart extends React.Component<EmployeeProps, EmployeeErrorPerMonthChartState> {
    state: EmployeeErrorPerMonthChartState = {
        errors: [],
        data: [],
        errorPerMonth: {
            series: [
                {
                    data: [],
                    name: "Mensal",
                },
            ],
            options: {
                chart: {
                    type: "bar",
                    height: 350,
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                },
                plotOptions: {
                    bar: {
                        borderRadius: 2,
                        horizontal: false,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                xaxis: {
                    categories: [],
                    tickPlacement: "on",
                },
                tooltip: {
                    y: {
                        formatter: (val, opts) => {
                            const data = this.state.data?.[opts.seriesIndex]?.[opts.dataPointIndex]
                            if (this.state.errors.length > 0) return `${MethodsUtils.formatCurrency(data?.value || 0)} (${val})`;
                            return `${val}`;
                        },
                    }
                }
            },
        },
        selectedEmployees: [],
        selectedErrors: [],
        selectedYear: ['2024'],
        loadingButton: false,
        selectFullData: [],
    };

    componentDidMount() {
        this.generateData();
    }

    generateData = (): void => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const startYear = 2024;

        type MonthNode = { label: string; value: string };
        type YearNode = { label: string; value: string; children: MonthNode[] };

        const data: YearNode[] = [];

        for (let year = startYear; year <= currentYear; year++) {
            const yearNode: YearNode = {
                label: `${year}`,
                value: `${year}`,
                children: [],
            };

            const monthsInYear = year === currentYear ? currentMonth : 12;
            for (let month = 1; month <= monthsInYear; month++) {
                const monthLabel = new Date(year, month - 1).toLocaleString("default", {
                    month: "long",
                });

                yearNode.children.push({
                    label: monthLabel,
                    value: `${year}-${month.toString().padStart(2, "0")}`,
                });
            }

            data.push(yearNode);
        }

        this.setState({ selectFullData: data });
    };

    handleSetLoadingTrue = () => {
        this.setState({ loadingButton: true });
    };

    handleSetLoadingFalse = () => {
        this.setState({ loadingButton: false });
    };

    handleBarChartPerMonth = async () => {
        this.handleSetLoadingTrue();

        const { selectedEmployees, selectedYear, selectedErrors } = this.state;

        if (!selectedYear || selectedYear.length === 0) {
            Notification.warning({
                title: "Aviso!",
                description: "Selecione um ano para gerar o gráfico.",
                duration: 3500,
            });

            this.handleSetLoadingFalse();
            return;
        }

        try {
            const response = await axios.get("/api/v1/dashboard/get_linechartpermonth_information", {
                params: {
                    employee: selectedEmployees,
                    fullPeriod: selectedYear,
                    lineChartFilterType: "employee",
                    store: [],
                    platform: [],
                    errors: selectedErrors
                },
            }
            );

            const { errors: errorData, data, series, categories } = response.data;

            // Validação defensiva
            if (!Array.isArray(series) || series.length === 0 || !Array.isArray(categories)) {
                this.setState({
                    errors: errorData,
                    data: data,
                    errorPerMonth: {
                        ...this.state.errorPerMonth,
                        series: [{ name: "Mensal", data: [] }],
                        options: {
                            ...this.state.errorPerMonth.options,
                            xaxis: {
                                ...this.state.errorPerMonth.options.xaxis,
                                categories: [],
                            },
                            noData: {
                                text: 'Nenhum dado encontrado',
                                align: 'center',
                                verticalAlign: 'middle',
                                style: {
                                    color: '#999',
                                    fontSize: '16px',
                                    fontFamily: 'sans-serif'
                                }
                            }
                        },
                    }
                });

                Notification.info({
                    title: "Sem dados",
                    description: "Nenhum dado foi encontrado para os filtros aplicados.",
                    duration: 3500,
                });

                return;
            }

            const formattedCategories = categories.map((date: any) => date ? dayjs(date).format("MM/YYYY") : "")

            this.setState({
                errors: errorData,
                data: data,
                errorPerMonth: {
                    ...this.state.errorPerMonth,
                    series: series,
                    options: {
                        ...this.state.errorPerMonth.options,
                        colors: handleGenerateHarmoniousColors(series.length),
                        xaxis: {
                            ...this.state.errorPerMonth.options.xaxis,
                            categories: formattedCategories,
                        },
                    },
                },
            });

        } catch (error) {
            Notification.error({
                title: "Erro!",
                description: "Erro ao buscar informações para o gráfico.",
                duration: 3500,
            });
            console.error("Erro ao pegar dados para o gráfico", error);
        } finally {
            this.handleSetLoadingFalse();
        }
    };

    render() {
        return (
            <div className={styles.mainContainer}>
                <div className={styles.containerFilters}>
                    <FormGroup style={{
                        width: '100%'
                    }}>
                        <span>Funcionários</span>
                        <CheckTreePicker
                            data={this.props.employee}
                            placeholder="Selecione funcionários..."
                            style={{ width: "100%" }}
                            value={this.state.selectedEmployees}
                            onChange={(value) => this.setState({ selectedEmployees: value })}
                        />
                    </FormGroup>

                    <FormGroup style={{
                        width: '100%'
                    }}>
                        <span>Lançamentos</span>
                        <CheckPickerSelectFilter
                            onSelectFilter={(value) => this.setState({ selectedErrors: value })}
                            data={this.props.errors}
                            placeholder="Selecione Lançamentos..."
                            style={{ width: "100%" }}
                            value={this.state.selectedErrors}
                            onChange={(value) => this.setState({ selectedErrors: value })}
                        />
                    </FormGroup>

                    <FormGroup style={{
                        width: '100%'
                    }}>
                        <span>Ano</span>
                        <CheckTreePicker
                            data={this.state.selectFullData}
                            placeholder="Período para consulta"
                            style={{ width: "100%" }}
                            value={this.state.selectedYear}
                            onChange={(value) => this.setState({ selectedYear: value })}
                            locale={{
                                ...CheckTreePicker.defaultProps?.locale,
                                checkAll: 'Ano Todo',
                                selectedValues: 'Tudo',
                            }}
                        />
                    </FormGroup>

                    <div className={styles.filterButtons}>
                        <span>ㅤ</span>
                        <Button
                            style={{ width: '250px' }}
                            appearance="primary"
                            color="blue"
                            loading={this.state.loadingButton}
                            onClick={this.handleBarChartPerMonth}
                        >
                            Filtrar
                        </Button>
                    </div>
                </div>

                <div className={styles.lineChartContainer}>
                    <ReactApexChart
                        options={this.state.errorPerMonth.options}
                        series={this.state.errorPerMonth.series}
                        type="bar"
                        height={350}
                    />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {this.state.errors.map((e: any) => {
                            const color = this.state.errorPerMonth.series.find(item => item.name === e.label)?.color
                            return (
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }} key={e.label}>
                                    <div style={{ width: 10, height: 10, borderRadius: 5, background: color || 'white' }}></div>
                                    <span><strong>{e.label}: </strong>{MethodsUtils.formatCurrency(e.value)} ({e.count})</span>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        );
    }
}

export default EmployeeErrorPerMonthChart;
